import React, { useState, useEffect } from "react";
import ReactSelect from "react-select";
import { 
  listTratamientos, 
  createTratamiento, 
  updateTratamiento,
  listTiposTratamiento
} from "../api/odontologia";
import { listClienteReceptor } from "../api/clientesReceptor";
import { listUsuarios } from "../api/usuarios";
import { showAlertError } from "../utils/alertHelper";
import Loader from "./Loader";
import { 
  FiActivity, FiPlus, FiSearch, FiEdit3, 
  FiCalendar, FiDollarSign, FiUser, FiBriefcase, FiFileText, FiTag
} from "react-icons/fi";

const TratamientosOdontologia = () => {
  const [loading, setLoading] = useState(true);
  const [tratamientos, setTratamientos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Catálogos
  const [pacientes, setPacientes] = useState([]);
  const [tiposTratamiento, setTiposTratamiento] = useState([]);
  const [doctores, setDoctores] = useState([]);

  // Búsqueda
  const [buscar, setBuscar] = useState("");
  
  const [form, setForm] = useState({
    id_receptor: "",
    id_tipo_tratamiento: "",
    id_usuario: "",
    estado: "activo",
    fecha_inicio: new Date().toISOString().split('T')[0],
    fecha_fin: "",
    total_estimado: "",
    observaciones: ""
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await listTratamientos();
      setTratamientos(res?.data || []);
    } catch (error) {
      console.error("Error al cargar tratamientos:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogos = async () => {
    try {
      const [resPacientes, resTipos, resDoctores] = await Promise.all([
        listClienteReceptor(),
        listTiposTratamiento(),
        listUsuarios()
      ]);
      setPacientes(resPacientes?.data || []);
      setTiposTratamiento(resTipos?.data || []);
      setDoctores(resDoctores?.data || []);
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (showModal) {
      fetchCatalogos();
    }
  }, [showModal]);

  const handleOpenModal = (tratamiento = null) => {
    if (tratamiento) {
      setForm({ 
        id_receptor: tratamiento.id_receptor?.toString() || "",
        id_tipo_tratamiento: tratamiento.id_tipo_tratamiento?.toString() || "",
        id_usuario: tratamiento.id_usuario?.toString() || "",
        estado: tratamiento.estado || "activo",
        fecha_inicio: tratamiento.fecha_inicio ? tratamiento.fecha_inicio.split('T')[0] : "",
        fecha_fin: tratamiento.fecha_fin ? tratamiento.fecha_fin.split('T')[0] : "",
        total_estimado: tratamiento.total_estimado || "",
        observaciones: tratamiento.observaciones || ""
      });
      setCurrentId(tratamiento.id);
      setEditMode(true);
    } else {
      setForm({ 
        id_receptor: "",
        id_tipo_tratamiento: "",
        id_usuario: "",
        estado: "activo",
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_fin: "",
        total_estimado: "",
        observaciones: ""
      });
      setEditMode(false);
    }
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const dataToSend = {
        ...form,
        id_receptor: parseInt(form.id_receptor),
        id_tipo_tratamiento: parseInt(form.id_tipo_tratamiento),
        id_usuario: parseInt(form.id_usuario),
        total_estimado: parseFloat(form.total_estimado) || 0,
        fecha_inicio: form.fecha_inicio ? `${form.fecha_inicio} 00:00:00` : null,
        fecha_fin: form.fecha_fin ? `${form.fecha_fin} 00:00:00` : null
      };

      if (editMode) {
        await updateTratamiento(currentId, dataToSend);
      } else {
        await createTratamiento(dataToSend);
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      showAlertError("Error", "No se pudo guardar el tratamiento.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectChange = (val, name) => {
    setForm(prev => ({ ...prev, [name]: val ? val.value : "" }));
  };

  const filteredTratamientos = tratamientos.filter(t => 
    t.ClientesReceptor?.nombre?.toLowerCase().includes(buscar.toLowerCase()) || 
    t.OdontologiaTipoTratamiento?.nombre?.toLowerCase().includes(buscar.toLowerCase())
  );

  const opcionesPacientes = pacientes.map(p => ({ value: p.id.toString(), label: p.nombre }));
  const opcionesTiposTratamiento = tiposTratamiento.map(t => ({ value: t.id.toString(), label: t.nombre }));
  const opcionesDoctores = doctores.map(d => ({ value: d.id.toString(), label: `${d.nombre} ${d.apellido || ""}` }));
  const opcionesEstado = [
    { value: "activo", label: "Activo" },
    { value: "finalizado", label: "Finalizado" },
    { value: "cancelado", label: "Cancelado" },
    { value: "pendiente", label: "Pendiente" }
  ];

  if (loading && tratamientos.length === 0) return <Loader />;

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto font-sans">
      
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Tratamientos Odontológicos</h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Control y seguimiento de tratamientos clínicos</p>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors shadow-sm shadow-blue-200 font-medium flex items-center justify-center gap-2"
        >
          <FiPlus size={20} /> Nuevo Tratamiento
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-4 sm:mb-6 bg-white p-3 sm:p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-slate-400" />
          </div>
          <input 
            type="text"
            placeholder="Buscar por paciente o tipo de tratamiento..."
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-slate-50 focus:bg-white text-sm"
          />
        </div>
      </div>

      {/* LISTA */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 bg-slate-50 uppercase border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 font-semibold">Paciente</th>
                <th className="px-4 py-3 font-semibold">Tipo de Tratamiento</th>
                <th className="px-4 py-3 font-semibold text-center">Fecha Inicio</th>
                <th className="px-4 py-3 font-semibold text-center">Total Estimado</th>
                <th className="px-4 py-3 font-semibold text-center">Estado</th>
                <th className="px-4 py-3 font-semibold text-center sticky right-0 bg-slate-100 border-l border-slate-200 z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">Opciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredTratamientos.map((t) => (
                <tr key={t.id} className="hover:bg-blue-50/50 transition-colors group">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 shrink-0">
                        <FiUser size={16} />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-slate-700 font-medium truncate">{t.ClientesReceptor?.nombre}</span>
                        <span className="text-[10px] text-slate-400 font-mono">{t.ClientesReceptor?.numeroDocumento || "Sin Documento"}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-slate-700 font-medium">{t.OdontologiaTipoTratamiento?.nombre}</span>
                      <span className="text-[10px] text-slate-400 flex items-center gap-1">
                        <FiFileText size={10} /> ID: {t.id_tipo_tratamiento}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    <div className="flex flex-col items-center">
                      <span className="text-slate-600 font-medium">
                        {t.fecha_inicio ? new Date(t.fecha_inicio).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : "---"}
                      </span>
                      {t.fecha_fin && (
                        <span className="text-[9px] text-slate-400 underline italic">
                          Fin: {new Date(t.fecha_fin).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    <span className="text-blue-600 font-semibold">${parseFloat(t.total_estimado).toFixed(2)}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border
                      ${t.estado === "activo" ? "bg-emerald-50 text-emerald-600 border-emerald-200" : 
                        t.estado === "finalizado" ? "bg-blue-50 text-blue-600 border-blue-200" : 
                        t.estado === "cancelado" ? "bg-rose-50 text-rose-600 border-rose-200" : 
                        "bg-amber-50 text-amber-600 border-amber-200"}`}>
                      {t.estado}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center sticky right-0 bg-slate-100 group-hover:bg-slate-200 border-l border-slate-200 z-0 transition-colors">
                    <button 
                      onClick={() => handleOpenModal(t)}
                      className="bg-white border border-slate-200 text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-50 hover:text-blue-600 transition-colors text-xs font-medium"
                    >
                      <FiEdit3 className="inline mr-1" /> Editar
                    </button>
                  </td>
                </tr>
              ))}
              {filteredTratamientos.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-400 italic">No se encontraron tratamientos registrados.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL PRINCIPAL */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-start sm:items-center z-[100] p-2 sm:p-6 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl flex flex-col my-4 sm:my-8 animate-fade-in-up">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                  <FiTag size={20} />
                </div>
                <h3 className="text-xl font-bold text-slate-800">
                  {editMode ? "Actualizar Tratamiento" : "Registrar Nuevo Tratamiento"}
                </h3>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar max-h-[65vh]">
              <form onSubmit={handleSubmit} id="tratamiento-form" className="space-y-6">
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Paciente (Receptor)</label>
                        <ReactSelect 
                          options={opcionesPacientes}
                          value={opcionesPacientes.find(o => o.value == form.id_receptor) || null}
                          onChange={(val) => handleSelectChange(val, "id_receptor")}
                          placeholder="Seleccione paciente..."
                          className="text-sm"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Tipo de Tratamiento</label>
                        <ReactSelect 
                          options={opcionesTiposTratamiento}
                          value={opcionesTiposTratamiento.find(o => o.value == form.id_tipo_tratamiento) || null}
                          onChange={(val) => handleSelectChange(val, "id_tipo_tratamiento")}
                          placeholder="Seleccione tipo..."
                          className="text-sm"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Doctor Responsable</label>
                        <ReactSelect 
                          options={opcionesDoctores}
                          value={opcionesDoctores.find(o => o.value == form.id_usuario) || null}
                          onChange={(val) => handleSelectChange(val, "id_usuario")}
                          placeholder="Seleccione doctor..."
                          className="text-sm"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Fecha Inicio</label>
                        <input 
                          type="date" 
                          name="fecha_inicio"
                          value={form.fecha_inicio}
                          onChange={handleChange}
                          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 text-sm"
                          required
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Fecha Fin (Est.)</label>
                        <input 
                          type="date" 
                          name="fecha_fin"
                          value={form.fecha_fin}
                          onChange={handleChange}
                          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Total Estimado ($)</label>
                        <input 
                          type="number" 
                          step="0.01"
                          name="total_estimado"
                          value={form.total_estimado}
                          onChange={handleChange}
                          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 text-sm"
                          required
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Estado</label>
                        <ReactSelect 
                          options={opcionesEstado}
                          value={opcionesEstado.find(o => o.value == form.estado) || null}
                          onChange={(val) => handleSelectChange(val, "estado")}
                          className="text-sm"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          isSearchable={false}
                        />
                    </div>
                    <div className="col-span-full">
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Observaciones Médicas</label>
                        <textarea 
                          name="observaciones"
                          value={form.observaciones}
                          onChange={handleChange}
                          rows="3"
                          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="Detalles sobre el tratamiento..."
                        ></textarea>
                    </div>
                 </div>
              </form>
            </div>

            <div className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 rounded-b-2xl">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="w-full sm:w-auto px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-white text-sm"
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="tratamiento-form"
                disabled={submitting}
                className="w-full sm:w-auto px-6 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 text-sm flex items-center justify-center gap-2"
              >
                {submitting && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>}
                {editMode ? "Actualizar Tratamiento" : "Guardar Tratamiento"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TratamientosOdontologia;
